import { useField } from 'formik';
import React, { useState } from 'react';
import { TextInput } from '@carbon/react';
import { useMoney } from 'src/hooks/useMoney';

const THOUSAND = 1000;

/**
 * The one amount field. The Formik value is always the real amount; for VND the field edits
 * in thousands ("50" = 50.000 ₫) because nobody types six zeros at a till. A plain TextInput
 * with inputMode, not Carbon's NumberInput: NumberInput's steppers and its own min/max
 * clamping fight a display value that is not the stored value.
 *
 * `draft` holds exactly what the user typed while the field is being edited — deriving the
 * text from the stored number on every keystroke would eat a trailing "." ("12." → 12).
 */
const MoneyInput = ({
  name,
  label,
  min = 0,
  helperText,
}: {
  name: string;
  label: string;
  min?: number;
  helperText?: string;
}) => {
  const [field, meta, helpers] = useField<number | ''>(name);
  const { currency, decimals, usesThousandsShortcut, format, isKnown } = useMoney();
  const [draft, setDraft] = useState<string | null>(null);
  const factor = usesThousandsShortcut ? THOUSAND : 1;
  const stored = field.value === '' || field.value == null ? '' : String(Number(field.value) / factor);
  const suffix = usesThousandsShortcut ? 'K ₫' : currency === 'USD' ? '$' : currency;

  return (
    <TextInput
      id={name}
      name={name}
      labelText={`${label} (${suffix})`}
      inputMode="decimal"
      value={draft ?? stored}
      disabled={!isKnown}
      onBlur={(event) => {
        setDraft(null);
        field.onBlur(event);
      }}
      onChange={(event) => {
        if (!isKnown) {
          return;
        }
        // VND types a Vietnamese decimal comma ("12,5" -> 12.5); every other currency's comma
        // is en-US thousands grouping ("1,000" -> 1000), which must be stripped, not read as
        // a decimal point, or "1,000" would parse as 1 and "1,000,000" as NaN.
        const raw = usesThousandsShortcut
          ? event.target.value.replace(',', '.').trim()
          : event.target.value.replace(/,/g, '').trim();
        setDraft(event.target.value);
        if (raw === '') {
          helpers.setValue('');
          return;
        }
        const typed = Number(raw);
        if (Number.isNaN(typed)) {
          return;
        }
        const scale = 10 ** decimals;
        helpers.setValue(Math.round(typed * factor * scale) / scale);
      }}
      helperText={
        helperText ?? (field.value !== '' && field.value != null ? `= ${format(Number(field.value))}` : undefined)
      }
      invalid={!!(meta.touched && meta.error)}
      invalidText={meta.error}
      min={min}
    />
  );
};

export default MoneyInput;
